import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Button from '../../components/ui/Button';
import Icon from '../../components/ui/AppIcon.jsx';
import { getInstrument, createCalibration } from '../../utils/api';
import { supabase } from '../../utils/supabase';
import { createPressureManometerSession } from '../../utils/api-pressure-manometer-pilot';
import { createForceSession } from '../../utils/api-force-calibration';
import { createGasSession } from '../../utils/api-gas-calibration';
import { createSafetyValveSession } from '../../utils/api-safety-valve-calibration';
import { createCheckValveSession } from '../../utils/api-check-valve-calibration';
import { createTemperatureSession } from '../../utils/api-temperature-calibration';
import { createFlowSession } from '../../utils/api-flow-calibration';
import { createDimensionalSession } from '../../utils/api-dimensional-calibration';
import { createEnvironmentalSession } from '../../utils/api-environmental-calibration';
import { createAnalyticalSession } from '../../utils/api-analytical-calibration';
import {
  getCalibrationTemplateFamilies,
  getCalibrationTemplateProfiles,
  resolveCalibrationTemplateProfile,
  resolveCalibrationTemplateProfileByCode,
} from '../../utils/calibration-template-catalog';

function countProfiles(subgroups = []) {
  return subgroups.reduce((sum, subgroup) => sum + (subgroup.profiles?.length || 0), 0);
}

function findLocation(families, templateCode) {
  for (const family of families) {
    for (const subgroup of family.subgroups || []) {
      const profile = (subgroup.profiles || []).find((item) => item.templateCode === templateCode);
      if (profile) {
        return {
          familyKey: family.key,
          subgroupKey: subgroup.key,
          profile,
        };
      }
    }
  }
  return null;
}

export default function PressureManometerTemplatePage() {
  const { instrumentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState(searchParams.get('templateCode') || '');
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [expandedSubgroup, setExpandedSubgroup] = useState(null);

  const serviceOrderCode = searchParams.get('serviceOrderCode') || '';
  const templateProfiles = useMemo(() => getCalibrationTemplateProfiles(), []);
  const families = useMemo(() => getCalibrationTemplateFamilies(), []);

  const recommendedProfile = useMemo(() => {
    if (!instrument) return null;
    return resolveCalibrationTemplateProfile({ instrument });
  }, [instrument]);

  const activeProfile = useMemo(() => {
    return (
      resolveCalibrationTemplateProfileByCode(selectedTemplateCode) ||
      recommendedProfile ||
      templateProfiles[0] ||
      null
    );
  }, [selectedTemplateCode, recommendedProfile, templateProfiles]);

  const activeLocation = useMemo(() => {
    if (!activeProfile?.templateCode) return null;
    return findLocation(families, activeProfile.templateCode);
  }, [families, activeProfile]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const inst = await getInstrument(instrumentId);
        setInstrument(inst);
      } catch (e) {
        console.error('[TemplateCatalog] load error', e);
        setError(e?.message || 'No se pudo cargar el instrumento.');
      } finally {
        setLoading(false);
      }
    })();
  }, [instrumentId]);

  useEffect(() => {
    if (!instrument) return;

    const location = recommendedProfile ? findLocation(families, recommendedProfile.templateCode) : null;

    if (!selectedTemplateCode && recommendedProfile?.templateCode) {
      setSelectedTemplateCode(recommendedProfile.templateCode);
    }

    if (!expandedFamily) {
      setExpandedFamily(location?.familyKey || activeLocation?.familyKey || families[0]?.key || null);
    }

    if (!expandedSubgroup) {
      setExpandedSubgroup(location?.subgroupKey || activeLocation?.subgroupKey || families[0]?.subgroups?.[0]?.key || null);
    }
  }, [instrument, families, recommendedProfile, activeLocation, selectedTemplateCode, expandedFamily, expandedSubgroup]);

  const handleContinue = async () => {
    if (!instrumentId || !activeProfile) return;

    try {
      setCreating(true);

      if (activeProfile.workflow === 'force_torque') {
        const created = await createForceSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        navigate(`/ops/calibrations/force/torque/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'temperature_comparison') {
        const created = await createTemperatureSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        const temperaturePath = activeProfile.templateCode === 'TEMP-IR:pyrometer:v1'
          ? 'infrared'
          : activeProfile.templateCode === 'TEMP-SENSOR:rtd_thermocouple:v1'
            ? 'sensor'
            : activeProfile.templateCode === 'TEMP-CHAMBER:oven_chamber:v1'
              ? 'chamber'
              : 'contact';
        navigate(`/ops/calibrations/temperature/${temperaturePath}/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'flow_comparison' || activeProfile.workflow === 'flow_mass') {
        const created = await createFlowSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        const flowPath = activeProfile.templateCode === 'FLOW-MASS:coriolis:v1'
          ? 'mass'
          : activeProfile.templateCode === 'FLOW-VOLUME:mechanical_totalizer:v1'
            ? 'mechanical-totalizer'
            : activeProfile.templateCode === 'FLOW-VOLUME:ultrasonic:v1'
              ? 'ultrasonic'
              : activeProfile.templateCode === 'FLOW-VOLUME:rotameter:v1'
                ? 'rotameter'
                : 'electromagnetic';
        navigate(`/ops/calibrations/flow/${flowPath}/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'dimensional_comparison') {
        const created = await createDimensionalSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        navigate(`/ops/calibrations/dimensional/${activeProfile.profileKey || 'caliper'}/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'environmental_comparison') {
        const created = await createEnvironmentalSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        const path = activeProfile.templateCode === 'ENV-LIGHT:luxmeter:v1'
          ? 'luxmeter'
          : activeProfile.templateCode === 'ENV-SOUND:sound_level_meter:v1'
            ? 'sound-level-meter'
            : 'anemometer';
        navigate(`/ops/calibrations/environmental/${path}/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'analytical_comparison') {
        const created = await createAnalyticalSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        const pathByProfile = {
          ph_meter: 'ph-meter', conductivity_meter: 'conductivity-meter', turbidimeter: 'turbidimeter',
          oximeter: 'oximeter', refractometer: 'refractometer', alcohol_meter: 'alcohol-meter',
          density_meter: 'density-meter', must_meter: 'must-meter', volumetric: 'volumetric',
        };
        navigate(`/ops/calibrations/analytical/${pathByProfile[activeProfile.profileKey] || 'ph-meter'}/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'gas_detector') {
        const created = await createGasSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        navigate(`/ops/calibrations/gas/detector/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'safety_valve') {
        const created = await createSafetyValveSession({
          instrumentId,
          serviceOrderCode,
          templateCode: activeProfile.templateCode,
          certificateType: 'NO_SAC',
        });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        navigate(`/ops/calibrations/valve/safety/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'check_valve') {
        const created = await createCheckValveSession({ instrumentId, serviceOrderCode, certificateType: 'NO_SAC' });
        const q = new URLSearchParams();
        q.set('sessionId', created.id);
        q.set('templateCode', activeProfile.templateCode);
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        navigate(`/ops/calibrations/valve/check/${instrumentId}?${q.toString()}`);
        return;
      }

      if (activeProfile.workflow === 'calibration_editor') {
        const { data: userData } = await supabase.auth.getUser();
        const user = userData?.user || null;
        const calibration = await createCalibration({
          instrument_id: instrumentId,
          date: new Date().toISOString(),
          technician_id: user?.id || null,
          technician_name: user?.user_metadata?.full_name || user?.email || null,
          status: 'in-progress',
        });

        const q = new URLSearchParams();
        q.set('calibrationId', calibration.id);
        q.set('view', 'editor');
        q.set('blockType', activeProfile.defaultBlockType || 'dimensional_comparison');
        if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
        navigate(`/instrument-details/${instrumentId}?${q.toString()}`);
        return;
      }

      const created = await createPressureManometerSession({
        instrumentId,
        certificateType: 'NO_SAC',
        templateCode: activeProfile.templateCode,
      });

      const q = new URLSearchParams();
      q.set('sessionId', created.id);
      if (serviceOrderCode) q.set('serviceOrderCode', serviceOrderCode);
      navigate(`/ops/calibrations/pressure/manometer/${instrumentId}?${q.toString()}`);
    } catch (e) {
      console.error('[TemplateCatalog] continue error', e);
      setError(e?.message || 'No se pudo iniciar la calibracion.');
    } finally {
      setCreating(false);
    }
  };

  const selectTemplate = (profile, familyKey, subgroupKey) => {
    setSelectedTemplateCode(profile.templateCode);
    setExpandedFamily(familyKey);
    setExpandedSubgroup(subgroupKey);
  };

  const toggleFamily = (familyKey, firstSubgroupKey) => {
    setExpandedFamily((prev) => (prev === familyKey ? null : familyKey));
    if (!selectedTemplateCode && firstSubgroupKey) {
      setExpandedSubgroup(firstSubgroupKey);
    }
  };

  const toggleSubgroup = (subgroupKey) => {
    setExpandedSubgroup((prev) => (prev === subgroupKey ? null : subgroupKey));
  };

  if (loading) {
    return <div className="p-6">Cargando plantillas...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Seleccionar familia y plantilla</h1>
          <p className="text-sm text-slate-200 mt-1">
            Instrumento: <strong>{instrument?.tag || instrument?.id}</strong> - {instrument?.brand} {instrument?.model}
          </p>
          <p className="text-xs text-slate-300 mt-1">
            Rango: <strong>{instrument?.range_min ?? '-'}</strong> a <strong>{instrument?.range_max ?? '-'}</strong>{' '}
            {instrument?.unit || 'bar'}
          </p>
          <p className="text-xs text-slate-300 mt-1">
            Primero se elige la familia y luego la subfamilia. El técnico puede usar cualquiera de las disponibles.
          </p>
          {serviceOrderCode ? (
            <p className="text-xs text-slate-300 mt-1">
              Orden de servicio: <strong>{serviceOrderCode}</strong>
            </p>
          ) : null}
        </div>

        <Button variant="secondary" onClick={() => navigate(`/instrument-details/${instrumentId}`)}>
          Volver a instrumento
        </Button>
      </div>

      {error ? (
        <div className="rounded border border-red-400 bg-red-50 text-red-900 px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      {recommendedProfile ? (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
          Recomendación: <strong>{recommendedProfile.label}</strong>. El técnico puede usar otro patrón si el caso lo requiere.
        </div>
      ) : null}

      <div className="space-y-4">
        {families.map((family) => {
          const familyCount = countProfiles(family.subgroups);
          const isFamilyOpen = expandedFamily === family.key || (expandedFamily == null && family.key === (activeLocation?.familyKey || families[0]?.key));
          const familySelected = activeLocation?.familyKey === family.key;
          const familyRecommended = recommendedProfile ? recommendedProfile.family === family.key : false;

          return (
            <div key={family.key} className="rounded-xl border border-border bg-card overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left hover:bg-muted/30 transition"
                onClick={() => toggleFamily(family.key, family.subgroups[0]?.key)}
              >
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-semibold">{family.label}</h2>
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-500/15 text-slate-200 border border-slate-500/30">
                      {familyCount} plantilla{familyCount === 1 ? '' : 's'}
                    </span>
                    {familyRecommended ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                        Recomendado
                      </span>
                    ) : null}
                    {familySelected ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                        Activa
                      </span>
                    ) : null}
                  </div>
                  <p className="text-sm text-slate-300 mt-1">{family.description}</p>
                </div>
                <Icon name={isFamilyOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
              </button>

              {isFamilyOpen ? (
                <div className="border-t border-border p-4 space-y-4">
                  {family.subgroups.map((subgroup) => {
                    const subgroupSelected = activeLocation?.subgroupKey === subgroup.key;
                    const subgroupRecommended = recommendedProfile
                      ? recommendedProfile.family === family.key && recommendedProfile.subfamily === subgroup.key
                      : false;
                    const isSubgroupOpen = expandedSubgroup === subgroup.key || (expandedSubgroup == null && subgroupSelected);
                    const subgroupProfiles = subgroup.profiles || [];

                    return (
                      <div key={subgroup.key} className="rounded-lg border border-border/70 bg-background/50 overflow-hidden">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30 transition"
                          onClick={() => toggleSubgroup(subgroup.key)}
                        >
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold">{subgroup.label}</h3>
                              <span className="text-xs px-2 py-1 rounded-full bg-slate-500/15 text-slate-200 border border-slate-500/30">
                                {subgroupProfiles.length} plantilla{subgroupProfiles.length === 1 ? '' : 's'}
                              </span>
                              {subgroupRecommended ? (
                                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                                  Recomendado
                                </span>
                              ) : null}
                              {subgroupSelected ? (
                                <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                                  Activa
                                </span>
                              ) : null}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">{subgroup.description}</p>
                          </div>
                          <Icon name={isSubgroupOpen ? 'ChevronUp' : 'ChevronDown'} size={16} />
                        </button>

                        {isSubgroupOpen ? (
                          <div className="border-t border-border/60 p-4">
                            {subgroupProfiles.length ? (
                              <div className="grid gap-4 md:grid-cols-2">
                                {subgroupProfiles.map((profile) => {
                                  const isSelected = profile.templateCode === activeProfile?.templateCode;
                                  const isRecommended = profile.templateCode === recommendedProfile?.templateCode;
                                  const flowLabel = profile.workflow === 'calibration_editor'
                                    ? 'Editor de calibración'
                                    : profile.workflow === 'force_torque'
                                      ? 'Módulo Fuerza / Torque'
                                      : profile.workflow === 'gas_detector'
                                        ? 'Módulo de Gases'
                                        : profile.workflow === 'safety_valve'
                                          ? 'Módulo Válvulas de Seguridad'
                                        : profile.workflow === 'temperature_comparison'
                                          ? 'Módulo de Temperatura'
                                        : profile.workflow === 'flow_comparison' || profile.workflow === 'flow_mass'
                                          ? 'Módulo de Caudal'
                                        : profile.workflow === 'environmental_comparison'
                                          ? 'Módulo SSHH / Ambiental'
                                        : profile.workflow === 'analytical_comparison'
                                          ? 'Módulo de Analítica'
                                        : profile.workflow === 'check_valve'
                                          ? 'Módulo Válvulas Antirretorno'
                                      : 'Sesión metrológica';

                                  return (
                                    <button
                                      key={profile.templateCode}
                                      type="button"
                                      onClick={() => selectTemplate(profile, family.key, subgroup.key)}
                                      className={`text-left rounded-xl border p-5 transition ${
                                        isSelected ? 'border-primary bg-primary/10' : 'border-border bg-background hover:bg-muted/40'
                                      }`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="text-lg font-semibold">{profile.label}</h4>
                                            {isRecommended ? (
                                              <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                                                Recomendado
                                              </span>
                                            ) : null}
                                          </div>
                                          <p className="text-sm text-slate-300 mt-1">{profile.displayName}</p>
                                        </div>
                                        <Icon name={isSelected ? 'CheckCircle' : 'Circle'} size={18} />
                                      </div>

                                      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                                        <div className="rounded border border-border/70 bg-background/70 p-3">
                                          <p className="text-xs text-slate-400">Revisión</p>
                                          <p className="font-medium">{profile.templateRevision}</p>
                                        </div>
                                        <div className="rounded border border-border/70 bg-background/70 p-3">
                                          <p className="text-xs text-slate-400">Flujo</p>
                                          <p className="font-medium">{flowLabel}</p>
                                        </div>
                                      </div>

                                      <p className="mt-4 text-xs text-slate-400">
                                        {profile.summary || 'Se usará este patrón para crear la sesión de calibración.'}
                                      </p>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-sm text-slate-300">
                                No hay plantillas cargadas todavía para esta subfamilia.
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate(`/instrument-details/${instrumentId}`)}>
          Cancelar
        </Button>
        <Button onClick={handleContinue} disabled={creating || !activeProfile}>
          {creating ? 'Iniciando...' : 'Continuar'}
        </Button>
      </div>
    </div>
  );
}
